import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { AccessDeniedRedirect } from '@/core/components/AccessDeniedRedirect';
import { useLocation as useLocationEntity } from '@/entities/location/hooks/useLocations';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import { useTreasuryPermissions } from '@/modules/treasury/hooks/useTreasuryPermissions';

type TreasuryCapability = 'view' | 'manage' | 'banking' | 'reconciliation';
type ScopeType = 'company' | 'location';

const TREASURY_CODENAME: Record<TreasuryCapability, string> = {
  view: 'location.view_treasury',
  manage: 'location.manage_treasury',
  banking: 'location.manage_treasury_banking',
  reconciliation: 'location.manage_treasury_reconciliation',
};

/**
 * TreasuryAccessGate — wraps treasury surfaces with a permission check.
 *
 * When `fallbackTo` is omitted (the default) a denial renders the
 * editorial 403 page (`/access-denied`) with the original URL preserved
 * as `?return=` and the canonical permission codename surfaced as the
 * `?missing=` diagnostic. Pass an explicit `fallbackTo` only when a
 * silent bounce is the intended UX.
 */
export function TreasuryAccessGate({
  capability = 'view',
  scopeType,
  locationId,
  children,
  fallbackTo,
}: {
  capability?: TreasuryCapability;
  scopeType?: ScopeType;
  locationId?: string | null;
  children: ReactNode;
  fallbackTo?: string;
}) {
  const permissions = useTreasuryPermissions({ scopeType, locationId });
  const { activeLocationId } = useActiveContext();
  // Light fetch — almost always cached. Used only to populate the
  // editorial 403 page's `?scope=` diagnostic pill. Skipped for
  // company-scoped denials (the codename already starts with `company.`,
  // there's no per-location scope to surface).
  const scopeLocationId =
    scopeType === 'company' ? null : (locationId ?? activeLocationId ?? null);
  const { data: scopeLocation } = useLocationEntity(scopeLocationId ?? undefined);

  const allowed = capability === 'view'
    ? permissions.canViewTreasury
    : capability === 'manage'
      ? permissions.canManageTreasury
      : capability === 'banking'
        ? permissions.canManageTreasuryBanking
        : permissions.canManageTreasuryReconciliation;

  if (!allowed) {
    if (fallbackTo) {
      return <Navigate to={fallbackTo} replace />;
    }
    return (
      <AccessDeniedRedirect
        missing={TREASURY_CODENAME[capability]}
        scope={scopeLocation?.name}
      />
    );
  }

  return <>{children}</>;
}
